import { Breakpoint } from '@altibox/lte-salgskanaler-design-system-component-lib';
import React, { FunctionComponent } from 'react';

import { InformationCard } from 'src/components/information-card';
import { useCustomerInfoQueryStateOrThrow } from 'src/hooks/use-customer-info-query';
import { useSelectSubmitShoppingCartState } from 'src/hooks/use-submit-shopping-cart-mutation';
import { selectSelectedServiceLocationOrThrow } from 'src/state/address/selectors';
import { selectItemQuantitiesFromCart } from 'src/state/cart/selectors';
import { useAppSelector } from 'src/state/thunk-api';
import { i18n } from 'src/i18n';
import { DateUtils } from 'src/utils/date-utils';
import { OrderInformationUtils } from 'src/utils/order-information-utils';
import { I18nUtils } from 'src/utils/i18n-utils';

import {
  StyledDescription,
  StyledOrderDetails,
  StyledProducts,
  StyledProductText,
  StyledProductTitle,
  StyledTitle,
} from './order-details.styled';
import { OrderDetailsRow } from './order-details-row';
import { DetailsRow } from './details-row';

export const OrderDetails: FunctionComponent = () => {
  const { orderConfirmation } = useSelectSubmitShoppingCartState();
  const {
    userInfo: { userDetails },
  } = useCustomerInfoQueryStateOrThrow();
  const serviceLocation = useAppSelector(selectSelectedServiceLocationOrThrow);
  const itemQuantitiesFromCart = useAppSelector(selectItemQuantitiesFromCart);

  const { quantityUpgrade, quantityTotal, quantityPurchase } = itemQuantitiesFromCart;

  if (!orderConfirmation) {
    throw new Error('Order confirmation must be present.');
  }

  const renderProduct = (amount: number, productName: string): React.ReactNode => (
    <>
      {renderProductText(`${amount} ${i18n.page.success.orderDetails.amount}`)}
      {renderProductText(productName)}
    </>
  );

  const renderProductText = (text: React.ReactNode): React.ReactNode => (
    <StyledProductText component="p" variant="paragraph3" maxBreakpoint={Breakpoint.TABLET}>
      {text}
    </StyledProductText>
  );

  return (
    <InformationCard $showBordersForMobile={true}>
      <StyledTitle component="h2" variant="paragraph1" maxBreakpoint={Breakpoint.TABLET}>
        {i18n.page.success.orderDetails.title}
      </StyledTitle>
      <StyledDescription component="p" variant="paragraph3">
        {I18nUtils.message(i18n.page.success.orderDetails.description, {
          quantity: quantityTotal,
        })}
      </StyledDescription>
      <StyledOrderDetails>
        <OrderDetailsRow
          title={i18n.page.success.orderDetails.orderNumber}
          value={orderConfirmation.orderNumber}
          isValueSensitive={true}
        />
        <OrderDetailsRow
          title={i18n.page.success.orderDetails.date}
          value={DateUtils.toShortDate(orderConfirmation.requestedCompletionDate)}
        />
        <OrderDetailsRow
          title={i18n.page.success.orderDetails.customer}
          value={OrderInformationUtils.getFullName(userDetails)}
          isValueSensitive={true}
        />
        <OrderDetailsRow
          title={i18n.page.success.orderDetails.paymentMethod}
          value={i18n.page.success.orderDetails.paymentMethodChosen}
        />
        <OrderDetailsRow
          title={i18n.page.success.orderDetails.address}
          value={OrderInformationUtils.getFullAddress(serviceLocation)}
          isValueSensitive={true}
        />
      </StyledOrderDetails>
      <DetailsRow>
        <StyledProductTitle component="p" variant="paragraph3" maxBreakpoint={Breakpoint.TABLET}>
          {i18n.page.success.orderDetails.products}
        </StyledProductTitle>
        <StyledProducts>
          {quantityPurchase > 0 && renderProduct(quantityPurchase, i18n.page.success.orderDetails.productPurchase)}
          {quantityUpgrade > 0 && renderProduct(quantityUpgrade, i18n.page.success.orderDetails.productUpgrade)}
        </StyledProducts>
      </DetailsRow>
    </InformationCard>
  );
};
